import { foreignKey, integer, pgEnum, pgTable, text, unique, uuid, real, boolean, uniqueIndex, } from 'drizzle-orm/pg-core';
import { orgPrimaryKey, orgTableBase } from './org-id';
import { Team } from './roster/team';
import { LibraryExercise } from './exercise/exercises';
import { ExerciseMetric } from './metrics';
import { OneRepMaxFormula, WeightUnit } from './../enums';
import { Profile } from './profile';
import { isoTimestamp, sortOrder } from '../utils';
import { relations, sql } from 'drizzle-orm';
export const BenchmarkGridType = pgEnum('benchmark_grid_type', ['rep', 'kpi']);
export const UpdateType = pgEnum('update_type', ['none', 'tested']);
export const BenchmarkGrid = pgTable('benchmark_grid', {
    ...orgTableBase,
    teamId: uuid('team_id').notNull(),
    name: text('name').notNull(),
    type: BenchmarkGridType('type').notNull(),
    repCount: integer('rep_count').notNull(),
    updateType: UpdateType('update_type').notNull(),
    isDefault: boolean('is_default').notNull().default(false),
    deletedAt: isoTimestamp('deleted_at'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    teamFk: foreignKey({
        columns: [table.orgId, table.teamId],
        foreignColumns: [Team.orgId, Team.id],
    }),
    defaultUqIdx: uniqueIndex('default_benchmark_grid_uq_idx')
        .on(table.orgId, table.teamId)
        .where(sql `${table.isDefault} IS TRUE`),
    nameUqIdx: uniqueIndex('benchmark_grid_name_uq_idx')
        .on(table.orgId, table.teamId, table.name)
        .where(sql `${table.deletedAt} IS NULL`),
}));
export const BenchmarkGrid_Relations = relations(BenchmarkGrid, ({ many }) => ({
    coreExercises: many(BenchmarkCoreExercise),
    athleteExerciseBenchmarks: many(AthleteExerciseBenchmark),
    athleteBenchmarkSnapshots: many(AthleteBenchmarkSnapshot),
    gridMetrics: many(BenchmarkGridMetric),
    athleteKpiBenchmarks: many(AthleteKpiBenchmark),
}));
export const AthleteExerciseBenchmark = pgTable('athlete_exercise_benchmark', {
    ...orgTableBase,
    profileId: uuid('profile_id').notNull(),
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    formula: OneRepMaxFormula('formula').notNull(),
    value: real('value').notNull(),
    unit: WeightUnit('unit').notNull(),
    snapshotId: uuid('snapshot_id').notNull(),
    benchmarkGridId: uuid('benchmark_grid_id').notNull(),
}, (table) => ({
    pk: orgPrimaryKey(table),
    uq1: unique().on(table.orgId, table.profileId, table.benchmarkGridId, table.libraryExerciseId),
    profileFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    exerciseFk: foreignKey({
        columns: [table.orgId, table.libraryExerciseId],
        foreignColumns: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    snapshotFk: foreignKey({
        columns: [table.orgId, table.snapshotId],
        foreignColumns: [
            AthleteBenchmarkSnapshot.orgId,
            AthleteBenchmarkSnapshot.id,
        ],
    }),
    benchmarkGridFk: foreignKey({
        columns: [table.orgId, table.benchmarkGridId],
        foreignColumns: [BenchmarkGrid.orgId, BenchmarkGrid.id],
    }),
}));
export const AthleteBenchmarkSnapshot = pgTable('athlete_benchmark_snapshot', {
    ...orgTableBase,
    profileId: uuid('profile_id').notNull(),
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    date: isoTimestamp('date').notNull(),
    weight: real('weight').notNull(),
    unit: WeightUnit('unit').notNull(),
    benchmarkGridId: uuid('benchmark_grid_id').notNull(),
    workoutId: uuid('workout_id'),
    workoutSetId: uuid('workout_set_id'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    profileFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    exerciseFk: foreignKey({
        columns: [table.orgId, table.libraryExerciseId],
        foreignColumns: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    benchmarkGridFk: foreignKey({
        columns: [table.orgId, table.benchmarkGridId],
        foreignColumns: [BenchmarkGrid.orgId, BenchmarkGrid.id],
    }),
}));
export const AthleteExerciseBenchmark_Relations = relations(AthleteExerciseBenchmark, ({ one }) => ({
    benchmarkGrid: one(BenchmarkGrid, {
        fields: [
            AthleteExerciseBenchmark.orgId,
            AthleteExerciseBenchmark.benchmarkGridId,
        ],
        references: [BenchmarkGrid.orgId, BenchmarkGrid.id],
    }),
    snapshot: one(AthleteBenchmarkSnapshot, {
        fields: [
            AthleteExerciseBenchmark.orgId,
            AthleteExerciseBenchmark.snapshotId,
        ],
        references: [AthleteBenchmarkSnapshot.orgId, AthleteBenchmarkSnapshot.id],
    }),
}));
export const AthleteBenchmarkSnapshot_Relations = relations(AthleteBenchmarkSnapshot, ({ one }) => ({
    benchmarkGrid: one(BenchmarkGrid, {
        fields: [
            AthleteBenchmarkSnapshot.orgId,
            AthleteBenchmarkSnapshot.benchmarkGridId,
        ],
        references: [BenchmarkGrid.orgId, BenchmarkGrid.id],
    }),
}));
export const BenchmarkCoreExercise = pgTable('benchmark_core_exercise', {
    ...orgTableBase,
    teamId: uuid('team_id').notNull(),
    libraryExerciseId: uuid('library_exercise_id').notNull(),
    sortOrder,
    benchmarkGridId: uuid('benchmark_grid_id').notNull(),
}, (table) => ({
    pk: orgPrimaryKey(table),
    teamFk: foreignKey({
        columns: [table.orgId, table.teamId],
        foreignColumns: [Team.orgId, Team.id],
    }),
    exerciseFk: foreignKey({
        columns: [table.orgId, table.libraryExerciseId],
        foreignColumns: [LibraryExercise.orgId, LibraryExercise.id],
    }),
    uq1: unique().on(table.orgId, table.teamId, table.benchmarkGridId, table.libraryExerciseId),
    uq2: unique().on(table.orgId, table.teamId, table.benchmarkGridId, table.sortOrder),
    benchmarkGridFk: foreignKey({
        columns: [table.orgId, table.benchmarkGridId],
        foreignColumns: [BenchmarkGrid.orgId, BenchmarkGrid.id],
    }),
}));
export const BenchmarkCoreExercise_Relations = relations(BenchmarkCoreExercise, ({ one }) => ({
    team: one(Team, {
        fields: [BenchmarkCoreExercise.orgId, BenchmarkCoreExercise.teamId],
        references: [Team.orgId, Team.id],
    }),
    benchmarkGrid: one(BenchmarkGrid, {
        fields: [
            BenchmarkCoreExercise.orgId,
            BenchmarkCoreExercise.benchmarkGridId,
        ],
        references: [BenchmarkGrid.orgId, BenchmarkGrid.id],
    }),
}));
/**
 * KPI analog of {@link BenchmarkCoreExercise}: maps a numeric metric onto a KPI
 * benchmark grid. Deliberately has no `(orgId, teamId, metricId)` DB
 * constraint: the one-metric-per-team rule is enforced in the router (phase 2),
 * leaving room for multi-grid-per-metric later.
 */
export const BenchmarkGridMetric = pgTable('benchmark_grid_metric', {
    ...orgTableBase,
    teamId: uuid('team_id').notNull(),
    benchmarkGridId: uuid('benchmark_grid_id').notNull(),
    metricId: uuid('metric_id').notNull(),
    sortOrder,
}, (table) => ({
    pk: orgPrimaryKey(table),
    teamFk: foreignKey({
        columns: [table.orgId, table.teamId],
        foreignColumns: [Team.orgId, Team.id],
    }),
    metricFk: foreignKey({
        columns: [table.orgId, table.metricId],
        foreignColumns: [ExerciseMetric.orgId, ExerciseMetric.id],
    }),
    benchmarkGridFk: foreignKey({
        columns: [table.orgId, table.benchmarkGridId],
        foreignColumns: [BenchmarkGrid.orgId, BenchmarkGrid.id],
    }),
    uqMetric: unique().on(table.orgId, table.benchmarkGridId, table.metricId),
    uqSortOrder: unique().on(table.orgId, table.benchmarkGridId, table.sortOrder),
}));
export const BenchmarkGridMetric_Relations = relations(BenchmarkGridMetric, ({ one }) => ({
    team: one(Team, {
        fields: [BenchmarkGridMetric.orgId, BenchmarkGridMetric.teamId],
        references: [Team.orgId, Team.id],
    }),
    benchmarkGrid: one(BenchmarkGrid, {
        fields: [BenchmarkGridMetric.orgId, BenchmarkGridMetric.benchmarkGridId],
        references: [BenchmarkGrid.orgId, BenchmarkGrid.id],
    }),
    metric: one(ExerciseMetric, {
        fields: [BenchmarkGridMetric.orgId, BenchmarkGridMetric.metricId],
        references: [ExerciseMetric.orgId, ExerciseMetric.id],
    }),
}));
/**
 * Per-athlete KPI benchmark values. Unlike rep-max (which splits current vs.
 * snapshot), KPI collapses both: values are append-style, so every recorded
 * value is a new dated row and "current" is the most recent by `date`. Hence no
 * uniqueness constraint. `workoutId`/`workoutSetId` are nullable (a value may be
 * recorded outside a workout).
 */
export const AthleteKpiBenchmark = pgTable('athlete_kpi_benchmark', {
    ...orgTableBase,
    profileId: uuid('profile_id').notNull(),
    benchmarkGridId: uuid('benchmark_grid_id').notNull(),
    metricId: uuid('metric_id').notNull(),
    value: real('value').notNull(),
    unit: text('unit').notNull(),
    date: isoTimestamp('date').notNull(),
    workoutId: uuid('workout_id'),
    workoutSetId: uuid('workout_set_id'),
}, (table) => ({
    pk: orgPrimaryKey(table),
    profileFk: foreignKey({
        columns: [table.orgId, table.profileId],
        foreignColumns: [Profile.orgId, Profile.id],
    }),
    metricFk: foreignKey({
        columns: [table.orgId, table.metricId],
        foreignColumns: [ExerciseMetric.orgId, ExerciseMetric.id],
    }),
    benchmarkGridFk: foreignKey({
        columns: [table.orgId, table.benchmarkGridId],
        foreignColumns: [BenchmarkGrid.orgId, BenchmarkGrid.id],
    }),
}));
export const AthleteKpiBenchmark_Relations = relations(AthleteKpiBenchmark, ({ one }) => ({
    benchmarkGrid: one(BenchmarkGrid, {
        fields: [AthleteKpiBenchmark.orgId, AthleteKpiBenchmark.benchmarkGridId],
        references: [BenchmarkGrid.orgId, BenchmarkGrid.id],
    }),
    metric: one(ExerciseMetric, {
        fields: [AthleteKpiBenchmark.orgId, AthleteKpiBenchmark.metricId],
        references: [ExerciseMetric.orgId, ExerciseMetric.id],
    }),
}));
//# sourceMappingURL=benchmarks.js.map